// Vejr: fire årstider à 15 s. Vind er seedet (± 0,2), sol og forbrug følger tabellen.

import { nextInt, type RngState } from './rng';
import { SEASON_TICKS, YEAR_TICKS, type Season, type State, type WeatherState } from './types';

export const SEASON_NAMES = ['Forår', 'Sommer', 'Efterår', 'Vinter'] as const;

/** Promille. */
export const WIND_BASE_PM = [600, 400, 700, 800] as const;
export const SUN_PM = [500, 900, 400, 100] as const;
export const DEMAND_PM = [1000, 900, 1000, 1200] as const;
export const WIND_JITTER_PM = 200;

/** Lav vejret for et antal årstider frem. Bruger kun vejr-strømmen. */
export function makeWeather(rng: RngState, seasons: number): WeatherState {
  const windPm: number[] = [];
  const sunPm: number[] = [];
  for (let i = 0; i < seasons; i++) {
    const s = i % 4;
    const base = WIND_BASE_PM[s] ?? 600;
    const w = base + nextInt(rng, -WIND_JITTER_PM, WIND_JITTER_PM);
    windPm.push(Math.max(50, Math.min(1000, w)));
    sunPm.push(SUN_PM[s] ?? 500);
  }
  return { windPm, sunPm };
}

/** Absolut årstidsnummer for banens tid. */
export function seasonIndex(clockTick: number): number {
  return Math.floor(clockTick / SEASON_TICKS);
}

export function seasonOf(clockTick: number): Season {
  return (seasonIndex(clockTick) % 4) as Season;
}

export function yearOf(clockTick: number): number {
  return Math.floor(clockTick / YEAR_TICKS);
}

export function windPm(state: State): number {
  const i = seasonIndex(state.clockTick);
  const arr = state.weather.windPm;
  return arr[Math.min(i, arr.length - 1)] ?? 600;
}

export function sunPm(state: State): number {
  const i = seasonIndex(state.clockTick);
  const arr = state.weather.sunPm;
  return arr[Math.min(i, arr.length - 1)] ?? 500;
}

export function demandPm(state: State): number {
  return DEMAND_PM[seasonOf(state.clockTick)] ?? 1000;
}

/** Vejrudsigt: de næste n årstider fra nu (inklusive den nuværende). */
export function weatherForecast(state: State, n = 3): { season: Season; windPm: number; sunPm: number }[] {
  const i0 = seasonIndex(state.clockTick);
  const out: { season: Season; windPm: number; sunPm: number }[] = [];
  for (let k = 0; k < n; k++) {
    const i = Math.min(i0 + k, state.weather.windPm.length - 1);
    out.push({
      season: ((i0 + k) % 4) as Season,
      windPm: state.weather.windPm[i] ?? 600,
      sunPm: state.weather.sunPm[i] ?? 500,
    });
  }
  return out;
}
